import {
  AnalyticsIcon,
  ArrowUpIcon,
  HomeIcon,
  IntegrationIcon,
  ProductsIcon,
  SegmentIcon,
  SettingsIcon,
} from 'components/icons';
import {
  NavigationLink,
  NavigationLinkMultiMenu,
  OnboardingChecklist,
  SideBarTimeAndToggle,
  StoreSelector,
  UserAvatar,
} from 'components/sidebar';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import { logout, userDetailsState } from 'store/userDetailsSlice';
import { useStoreSubscriptionPlan } from 'utils/useStoreSubscriptionPlan';
import {
  OnboardingStatus,
  useUserOnboardingStatus,
} from 'utils/useUserOnboardingStatus';

export default function SideBarDefault() {
  const [sideBarOpen, setSideBarOpen] = useState(true);

  const { currentStore } = useAppSelector(userDetailsState);

  const userOnboarding = useUserOnboardingStatus();

  const dispatch = useAppDispatch();
  const { storeSubscriptionPlan, subscriptionPlans } =
    useStoreSubscriptionPlan();

  // const { isTablet } = useScreenWidth();

  const sideBarSize = sideBarOpen ? 'w-segments-296px' : 'w-24';
  const sideBarOverflow = sideBarOpen
    ? 'overflow-y-auto'
    : 'overflow-x-visible';

  useEffect(
    () => {
      // ** Turned off due to multimenu responsiveness pending
      // if (isTablet) {
      //   setSideBarOpen(false);
      // } else {
      //   setSideBarOpen(true);
      // }
    },
    [
      // isTablet
    ]
  );

  const isStoreOnTrialPlan =
    currentStore?.completeInfo?.active_sub?.type !== 'regular';

  const showUpgradeButton =
    storeSubscriptionPlan < subscriptionPlans.small_business ||
    isStoreOnTrialPlan;

  return (
    <main
      className={`flex flex-col h-screen justify-between bg-segments-dark scrollbars-hidden ${sideBarSize} ${sideBarOverflow} `}
    >
      <section className="relative">
        <div className="flex items-center justify-between">
          <Link
            className="block my-6 mx-7"
            to={{
              pathname: '/dashboard',
              search: currentStore.serializedStoreName,
            }}
          >
            <img
              width="40"
              height="40"
              src="/assets/logo-segments.png"
              alt="logo"
            />
          </Link>
          {showUpgradeButton && (
            <Link
              to={{
                pathname: '/account/change-plan',
                search: currentStore.serializedStoreName,
              }}
              className="flex items-center px-4 py-2 space-x-1 font-bold text-white text-segments-13px mx-7 bg-segments-accent-color-orange rounded-3xl"
            >
              <ArrowUpIcon className="w-4 h-4 text-white" />
              <p>Upgrade plan</p>
            </Link>
          )}
        </div>
        <div className="mx-8 mt-8">
          <StoreSelector fullSize={sideBarOpen} />
        </div>

        <OnboardingChecklist />

        <ul className="mx-4 mt-3 font-bold text-white">
          <li>
            <NavigationLink
              fullSize={sideBarOpen}
              icon={HomeIcon}
              to={{
                pathname:
                  userOnboarding.status === OnboardingStatus.COMPLETED
                    ? '/dashboard'
                    : '/dashboard/welcome',
                search: currentStore.serializedStoreName
                  ? currentStore.serializedStoreName
                  : '',
              }}
            >
              Dashboard
            </NavigationLink>
          </li>

          <li>
            <NavigationLink
              fullSize={sideBarOpen}
              icon={IntegrationIcon}
              to={{
                pathname: '/integrations',
                search: currentStore.serializedStoreName
                  ? currentStore.serializedStoreName
                  : '',
              }}
            >
              Integrations
            </NavigationLink>
          </li>
          <div
            className={
              userOnboarding.status === OnboardingStatus.IN_PROGRESS
                ? 'opacity-50 pointer-events-none'
                : ''
            }
          >
            <li>
              <NavigationLinkMultiMenu
                fullSize={sideBarOpen}
                icon={SegmentIcon}
                mainMenu="Segments"
                subMenus={[
                  {
                    name: 'My segments',
                    pathname: '/customers/segments',
                    search: currentStore.serializedStoreName
                      ? currentStore.serializedStoreName
                      : '',
                  },
                  {
                    name: '+ Create new',
                    pathname: '/customers/create',
                    search: currentStore.serializedStoreName
                      ? currentStore.serializedStoreName
                      : '',
                  },
                ]}
              />
            </li>
            <li>
              <NavigationLinkMultiMenu
                fullSize={sideBarOpen}
                icon={AnalyticsIcon}
                mainMenu="Analytics"
                subMenus={[
                  {
                    name: 'Executive summary',
                    pathname: '/analytics/summary',
                    search: currentStore.serializedStoreName
                      ? currentStore.serializedStoreName
                      : '',
                  },
                  {
                    name: 'Purchase timing',
                    pathname: '/analytics/purchase-timing',
                    search: currentStore.serializedStoreName
                      ? currentStore.serializedStoreName
                      : '',
                    subscriptionAccess: subscriptionPlans.small_business,
                  },
                  {
                    name: 'Customer cohorts',
                    pathname: '/analytics/cohort',
                    search: currentStore.serializedStoreName
                      ? currentStore.serializedStoreName
                      : '',
                    subscriptionAccess: subscriptionPlans.small_business,
                  },
                ]}
              />
            </li>
            <li>
              <NavigationLinkMultiMenu
                fullSize={sideBarOpen}
                icon={ProductsIcon}
                mainMenu="Products"
                subMenus={[
                  {
                    name: 'Product journey',
                    pathname: '/products/product-journey',
                    search: currentStore.serializedStoreName
                      ? currentStore.serializedStoreName
                      : '',
                    subscriptionAccess: subscriptionPlans.growth,
                  },
                  {
                    name: 'Product relations',
                    pathname: '/products/product-relations',
                    search: currentStore.serializedStoreName
                      ? currentStore.serializedStoreName
                      : '',
                    subscriptionAccess: subscriptionPlans.growth,
                  },
                  {
                    name: 'Product cohorts',
                    pathname: '/products/cohort',
                    search: currentStore.serializedStoreName
                      ? currentStore.serializedStoreName
                      : '',
                    subscriptionAccess: subscriptionPlans.growth,
                  },
                ]}
              />
            </li>
          </div>
        </ul>
      </section>
      <div className="relative">
        <section className="flex flex-col mx-2 mt-5 mb-7">
          <div className="mb-3 text-white">
            <NavigationLinkMultiMenu
              fullSize={sideBarOpen}
              icon={SettingsIcon}
              mainMenu="Settings"
              subMenus={[
                {
                  name: 'Manage stores',
                  pathname: '/account/stores',
                  search: currentStore.serializedStoreName,
                },
                {
                  name: 'Change plan',
                  pathname: '/account/change-plan',
                  search: currentStore.serializedStoreName,
                },
                {
                  name: 'Update name / email',
                  pathname: '/account',
                  search: currentStore.serializedStoreName,
                },
                {
                  name: 'Change password',
                  pathname: '/account/change-password',
                  search: currentStore.serializedStoreName,
                },
                {
                  name: 'Referrals',
                  pathname: '/account/referrals',
                  search: currentStore.serializedStoreName,
                },
                {
                  name: 'Log out',
                  pathname: '',
                  search: '',
                  callback: () => dispatch(logout()),
                },
              ]}
            />
          </div>
          <UserAvatar fullSize={sideBarOpen} />

          <SideBarTimeAndToggle
            sideBarOpen={sideBarOpen}
            setSideBarOpen={setSideBarOpen}
          />
        </section>
      </div>
    </main>
  );
}
